import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import confetti from "@hiseb/confetti"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { useNavigate } from "@tanstack/react-router"
import { motion } from "motion/react"
import {
	IconCheckFill18 as CheckIcon,
	IconBellOnFill18,
	IconEnvelopeArrowRightFill18,
	IconPenWritingFill18,
	IconUsersFill18,
	IconPaperPlane4Fill18 as PaperPlane,
} from "nucleo-ui-fill-18"
import {
	IconChevronDownOutline18 as ChevronDownIcon,
	IconArrowDownToLineOutline18 as Download,
	IconUsersOutline18 as Users,
} from "nucleo-ui-outline-18"
import { CSVLink } from "react-csv"
import z from "zod"

import { BROADCAST_FROM_LOCAL_PART } from "@productlane/api/src/lib/broadcast-email"
import { SENDER_NAME_MAX_LENGTH } from "@productlane/api/src/lib/constants"
import { EMAIL_MAXIMUM_LENGTH, pro } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { hasEnabledEmail } from "@productlane/lib/src/email-settings"
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	Button,
	Dialog,
	Form,
	FormInput,
	IconButton,
	Label,
	Tooltip,
	useZodForm,
} from "@productlane/ui"
import { Switch } from "@productlane/ui/src/components/form/switch/switch"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"

import type { NotifyRecipientEntity } from "./notify-recipients/types"
import { DefaultEditor } from "@/components/editor/default-editor"
import { CollapseArrowIcon } from "@/components/shared/collapse-arrow-icon"
import { SlackBroadcastChannelsPopover } from "@/components/shared/slack-broadcast-channels-popover"
import { toast } from "@/components/toast"
import { CHANGELOG_TITLE_MAXIMUM_LENGTH } from "@/constants"
import { api } from "@/lib/api"
import { useRequiredPlans } from "@/lib/plans"
import { resolvePortalSenderName } from "@/lib/portal-sender-name"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	changelogPublishDate,
	changelogPublishFlag,
} from "@/screens/app/changelog/changelog-order"
import { UpgradeButton } from "../upgrade-button"
import { showsBroadcastEmailDetails } from "./broadcast-email-details"
import { notifySendBlocker } from "./notify-send-blocker"

const COUNTDOWN_SECONDS = 10

const sendUpdateEmailDetailsSchema = z.object({
	subject: z
		.string()
		.max(
			CHANGELOG_TITLE_MAXIMUM_LENGTH,
			`Subject must be at most ${CHANGELOG_TITLE_MAXIMUM_LENGTH} characters`,
		),
	senderName: z
		.string()
		.max(
			SENDER_NAME_MAX_LENGTH,
			`Sender name must be at most ${SENDER_NAME_MAX_LENGTH} characters`,
		),
	fromLocalPart: z
		.string()
		.regex(
			/^[a-zA-Z0-9._-]+$/,
			"From (local part) must be a single word: letters, numbers, dots, hyphens or underscores only",
		)
		.max(
			EMAIL_MAXIMUM_LENGTH,
			`Email local part must be at most ${EMAIL_MAXIMUM_LENGTH} characters`,
		),
})

function parsePrimaryEmail(primaryEmail: string): {
	localPart: string
	domain: string
} {
	const atIndex = primaryEmail.indexOf("@")
	if (atIndex === -1) {
		return { localPart: primaryEmail, domain: "" }
	}
	return {
		localPart: primaryEmail.slice(0, atIndex),
		domain: primaryEmail.slice(atIndex + 1),
	}
}

interface SendOptions {
	sendEmail: boolean
	sendSlack: boolean
}

interface SendUpdateModalProps {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	entity: NotifyRecipientEntity
	title?: string
	description?: string
}

export const SendUpdateModal = ({
	isOpen,
	onOpenChange,
	entity,
	title = "Send update",
	description = "Contact recipients about the progress of this feature",
}: SendUpdateModalProps) => {
	const { passes: atLeastProPlan } = useRequiredPlans(pro.andUp)
	const workspace = useZeroWorkspace()
	const isSlackConfigured = !!workspace?.slackConnectSettings
	const isEmailConfigured = hasEnabledEmail(workspace?.emailSettings)

	const isChangelogUpdateModal = entity.type === "changelog"
	const sendEmailByDefault =
		isEmailConfigured &&
		(!isChangelogUpdateModal ||
			workspace?.changelogEmailNotifications !== false)

	const [sendEmail, setSendEmail] = useState(sendEmailByDefault)
	const [sendSlack, setSendSlack] = useState(isSlackConfigured)
	const [detailsOpen, setDetailsOpen] = useState(false)

	const form = useZodForm({
		schema: sendUpdateEmailDetailsSchema,
		mode: "onChange",
		defaultValues: {
			subject: "",
			senderName: "",
			fromLocalPart: "",
		},
	})

	// Every live address can send a broadcast; the row marked as broadcast
	// default is preselected, falling back to the primary.
	const fromOptions = useMemo(
		() =>
			(workspace?.emailSettings ?? [])
				.filter((address) => address.isEnabled)
				.map((address) => ({
					email: address.primaryEmail,
					isBroadcastDefault: address.isBroadcastDefault,
					isPrimary: address.isPrimary,
				}))
				.sort((a, b) => {
					if (a.isBroadcastDefault !== b.isBroadcastDefault)
						return a.isBroadcastDefault ? -1 : 1
					if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
					return a.email.localeCompare(b.email)
				}),
		[workspace?.emailSettings],
	)
	const [fromOverride, setFromOverride] = useState<string | null>(null)
	const fromEmail =
		fromOverride && fromOptions.some((option) => option.email === fromOverride)
			? fromOverride
			: fromOptions[0]?.email

	const parsedFrom = useMemo(
		() => (fromEmail ? parsePrimaryEmail(fromEmail) : null),
		[fromEmail],
	)

	// Content on a named portal is sent under that portal's name, not the workspace's
	const defaultSenderName = useMemo(
		() =>
			resolvePortalSenderName({
				portalInstanceId: entity.portalInstanceId,
				portalInstances: workspace?.portalInstances,
				workspaceName: workspace?.name,
			}),
		[entity.portalInstanceId, workspace?.portalInstances, workspace?.name],
	)

	useEffect(() => {
		if (isOpen && parsedFrom) {
			form.reset({
				subject: isChangelogUpdateModal ? `New: ${entity.name}` : entity.name,
				senderName: defaultSenderName,
				fromLocalPart: parsedFrom?.localPart ?? BROADCAST_FROM_LOCAL_PART,
			})
			setFromOverride(null)
		}
	}, [
		isOpen,
		defaultSenderName,
		parsedFrom,
		isChangelogUpdateModal,
		entity.name,
		form,
	])

	// Zero hydrates the workspace after the first render, so the switches follow
	// the resolved settings instead of the mount-time snapshot.
	useEffect(() => {
		setSendEmail(sendEmailByDefault)
	}, [sendEmailByDefault])

	useEffect(() => {
		setSendSlack(isSlackConfigured)
	}, [isSlackConfigured])

	const zero = useZero()

	const sendBulkEmailUpdateMutation = api.mail.sendBulkUpdate.useMutation()
	const undoBulkUpdateMutation = api.mail.undoBulkUpdate.useMutation()
	const sendTestEmailUpdateMutation = api.mail.sendTestUpdate.useMutation({
		onError: (error) => {
			toast.error({
				title: "Failed to send test email",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		},
	})

	const sendSlackBroadcastMutation =
		api.mail.triggerSlackBroadcast.useMutation()
	const sendTestSlackBroadcastMutation =
		api.mail.sendTestChangelogSlack.useMutation({
			onError: (error) => {
				toast.error({
					title: "Failed to send test Slack message",
					description:
						error instanceof Error ? error.message : "An error occurred",
				})
			},
		})

	const changelogEmailRecipientsCountQuery =
		api.mail.getChangelogSubscribedContactsCount.useQuery(undefined, {
			enabled: isChangelogUpdateModal,
		})

	const slackChannelsQuery = api.mail.getSlackChannelsForBroadcast.useQuery(
		undefined,
		{
			enabled: isChangelogUpdateModal,
		},
	)

	const nonChangelogEmailRecipientsCountQuery =
		api.mail.getLinkedSubscribedContactsCount.useQuery(
			{
				entityType: entity.type,
				entityId: entity.id,
			},
			{
				enabled: !isChangelogUpdateModal,
			},
		)

	const changelogEmailsQuery =
		api.mail.getChangelogSubscribedContactsEmails.useQuery(undefined, {
			enabled: isChangelogUpdateModal && isEmailConfigured && atLeastProPlan,
		})

	const nonChangelogEmailsQuery =
		api.mail.getLinkedSubscribedContactsEmails.useQuery(
			{
				entityType: entity.type,
				entityId: entity.id,
			},
			{
				enabled: !isChangelogUpdateModal && isEmailConfigured && atLeastProPlan,
			},
		)

	const changelogEmailRecipientsCount = useMemo(() => {
		if (!changelogEmailRecipientsCountQuery.data) return 0
		return changelogEmailRecipientsCountQuery.data
	}, [changelogEmailRecipientsCountQuery.data])

	const nonChangelogEmailRecipientsCount = useMemo(() => {
		if (!nonChangelogEmailRecipientsCountQuery.data) return 0
		return nonChangelogEmailRecipientsCountQuery.data
	}, [nonChangelogEmailRecipientsCountQuery.data])

	const slackChannelCount = useMemo(() => {
		if (!slackChannelsQuery.data) return 0
		return slackChannelsQuery.data.count
	}, [slackChannelsQuery.data])

	const csvData = useMemo(() => {
		const emails = isChangelogUpdateModal
			? (changelogEmailsQuery.data ?? [])
			: (nonChangelogEmailsQuery.data ?? [])
		return [["email"], ...emails.map((email) => [email])] as [
			["email"],
			...[string][],
		]
	}, [
		isChangelogUpdateModal,
		changelogEmailsQuery.data,
		nonChangelogEmailsQuery.data,
	])

	const navigate = useNavigate()
	const [message, setMessage] = useState("")
	const [isSending, setIsSending] = useState(false)
	const pendingSendRef = useRef<{
		message: string
		options: SendOptions
	} | null>(null)

	const handleUndo = useCallback(async () => {
		try {
			await undoBulkUpdateMutation.mutateAsync({
				entityId: entity.id,
				entityType: entity.type,
			})
			toast.info({
				title: "Send cancelled",
				description: "The notifications was not sent.",
			})
		} catch (error) {
			toast.error({
				title: "Failed to cancel",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		}
	}, [entity.id, undoBulkUpdateMutation, entity.type])

	const executeSend = useCallback(async () => {
		const options = {
			sendEmail: sendEmail && isEmailConfigured && atLeastProPlan,
			sendSlack:
				sendSlack &&
				isSlackConfigured &&
				atLeastProPlan &&
				isChangelogUpdateModal,
		}
		pendingSendRef.current = { message, options }

		if (!pendingSendRef.current) return

		const { message: pendingMessage } = pendingSendRef.current

		try {
			setIsSending(true)

			if (!isChangelogUpdateModal && !options.sendEmail) {
				return
			}

			if (isChangelogUpdateModal) {
				await mutateOk(
					zero.mutate(
						mutators.changelogs.update(changelogPublishDate(entity.id)),
					),
				)
			}

			// Trigger Inngest events immediately - backend will handle the 10-second countdown
			if (options.sendEmail) {
				const { subject: formSubject, senderName: formSenderName } =
					form.getValues()
				const builtFrom = fromEmail
				await sendBulkEmailUpdateMutation.mutateAsync({
					entityType: entity.type,
					entityName: entity.name,
					entityId: entity.id,
					message: pendingMessage,
					senderName: formSenderName.trim() || undefined,
					fromEmail: builtFrom || undefined,
					subject: formSubject.trim() || undefined,
				})
			}
			if (options.sendSlack && isChangelogUpdateModal) {
				await sendSlackBroadcastMutation.mutateAsync({
					changelogId: entity.id,
				})
			}

			if (isChangelogUpdateModal) {
				await mutateOk(
					zero.mutate(
						mutators.changelogs.update(changelogPublishFlag(entity.id)),
					),
				)
			}

			const parts: string[] = []
			if (
				options.sendEmail &&
				(changelogEmailRecipientsCount > 0 ||
					nonChangelogEmailRecipientsCount > 0)
			) {
				parts.push(
					`${isChangelogUpdateModal ? changelogEmailRecipientsCount : nonChangelogEmailRecipientsCount} ${changelogEmailRecipientsCount === 1 || nonChangelogEmailRecipientsCount === 1 ? "subscriber" : "subscribers"}`,
				)
			}
			if (options.sendSlack && isSlackConfigured) {
				parts.push(
					`${slackChannelCount} Slack ${slackChannelCount === 1 ? "channel" : "channels"}`,
				)
			}

			const positions = [
				{ x: window.innerWidth * 0.5, y: window.innerHeight * 0.6 },
				{ x: window.innerWidth * 0.25, y: window.innerHeight * 0.45 },
				{ x: window.innerWidth * 0.75, y: window.innerHeight * 0.4 },
			]
			positions.forEach((position, i) =>
				setTimeout(() => confetti({ position }), i * 200),
			)

			if (parts.length > 0) {
				toast.info({
					title: isChangelogUpdateModal ? "Changelog published" : "Update sent",
					description: `Notifications will be sent to ${parts.join(" and ")} in ${COUNTDOWN_SECONDS} seconds`,
					action: {
						label: "Undo",
						callback: handleUndo,
					},
					time: COUNTDOWN_SECONDS * 1000, // pass time in ms, 10 seconds
					showProgress: true,
				})
			} else {
				toast.success({
					title: "Changelog published",
					description: "Published without notifications.",
				})
			}

			setMessage("")
			onOpenChange(false)
		} catch (error) {
			toast.error({
				title: "Failed to send update",
				description:
					error instanceof Error ? error.message : "An error occurred",
			})
		} finally {
			setIsSending(false)
		}
	}, [
		entity,
		isSlackConfigured,
		onOpenChange,
		changelogEmailRecipientsCount,
		slackChannelCount,
		sendBulkEmailUpdateMutation,
		sendSlackBroadcastMutation,
		handleUndo,
		atLeastProPlan,
		isEmailConfigured,
		message,
		sendEmail,
		sendSlack,
		isChangelogUpdateModal,
		nonChangelogEmailRecipientsCount,
		form,
		fromEmail,
		zero,
	])

	const handleSendTestEmail = () => {
		toast.success({
			title: "Test email sent",
			description: "Check your inbox for the test email.",
		})
		const { subject: formSubject, senderName: formSenderName } =
			form.getValues()
		const builtFrom = fromEmail
		sendTestEmailUpdateMutation.mutate({
			entityType: entity.type,
			entityId: entity.id,
			entityName: entity.name,
			message,
			senderName: formSenderName.trim() || undefined,
			fromEmail: builtFrom || undefined,
			subject: formSubject.trim() || undefined,
		})
	}

	const handleSendTestSlack = () => {
		if (!isChangelogUpdateModal) return

		toast.success({
			title: "Test Slack message sent",
			description: "Check your Slack channel for the test message.",
		})
		sendTestSlackBroadcastMutation.mutate({
			changelogId: entity.id,
		})
	}

	const isAnyLoading = isSending

	const canUseEmail = isEmailConfigured && atLeastProPlan
	const showsEmailDetails = showsBroadcastEmailDetails({
		canUseEmail,
		hasFromAddress: !!parsedFrom,
	})
	const canUseSlack = isSlackConfigured && atLeastProPlan

	const sendBlocker = isChangelogUpdateModal
		? null
		: notifySendBlocker({
				atLeastProPlan,
				isEmailConfigured,
				sendEmail,
				recipientCount: nonChangelogEmailRecipientsCount,
				hasMessage: !!message.trim(),
			})

	const explainSendBlocker = () => {
		if (sendBlocker === "upgrade") {
			toast.info({
				id: "notify-blocked",
				title: "Email updates start on the Pro plan",
				description: `Upgrade to email the contacts behind this ${entity.type}`,
				icon: IconEnvelopeArrowRightFill18,
			})
			return
		}
		if (sendBlocker === "no-sending-address") {
			toast.info({
				id: "notify-blocked",
				title: "Add a sending address to email subscribers",
				description: "Updates go out from your own verified domain.",
				icon: IconEnvelopeArrowRightFill18,
				action: {
					label: "Email settings",
					callback: () => void navigate({ to: "/settings/emails" }),
				},
			})
			return
		}
		if (sendBlocker === "email-off") {
			toast.info({
				id: "notify-blocked",
				title: "Turn on email to send this update",
				description: "Email is the only channel this update uses.",
				icon: IconEnvelopeArrowRightFill18,
			})
			return
		}
		if (sendBlocker === "no-recipients") {
			toast.info({
				id: "notify-blocked",
				title: `No subscribed contacts are linked to this ${entity.type}`,
				description: `Link a customer request to the ${entity.type}, and its contact receives the update.`,
				icon: IconUsersFill18,
			})
			return
		}
		if (sendBlocker === "empty-message") {
			toast.info({
				id: "notify-blocked",
				title: "Write a message to send",
				description: "Subscribers receive exactly what you write here.",
				icon: IconPenWritingFill18,
			})
		}
	}

	const explainAlwaysOn = () => {
		toast.info({
			id: "changelog-in-app-always-on",
			title: "In-app notifications are always on",
			description: "Turn changelog notifications off in Widget settings.",
			icon: IconBellOnFill18,
			action: {
				label: "Widget settings",
				callback: () => void navigate({ to: "/settings/widget" }),
			},
		})
	}

	const descriptionWithCounts = (
		<div className="space-y-3">
			<div>{description}</div>
			<div
				className={cn(
					"my-2 flex select-none flex-col gap-3 border-y border-gray-200 py-5 dark:border-gray-700",
					{
						"border-b-0": !isChangelogUpdateModal,
					},
				)}
			>
				{isChangelogUpdateModal && (
					<div className="flex items-center justify-between gap-2">
						<div className="flex flex-1 items-center gap-2">
							<Tooltip label="Always on. Turn changelog notifications off in Widget settings.">
								{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
								<span className="flex" onClick={explainAlwaysOn}>
									<Switch
										name="notify-in-app"
										checked={true}
										disabled
										switchClassName="pointer-events-none"
										variant="branded"
										size="small"
									/>
								</span>
							</Tooltip>
							<span className="text-sm text-secondary">
								Notify contacts via the in-app widget
							</span>
						</div>
						<div className="flex items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => void navigate({ to: "/settings/widget" })}
							>
								Widget settings
							</Button>
						</div>
					</div>
				)}
				<div className="flex items-center justify-between gap-2">
					<div className="flex flex-1 items-center gap-2">
						<Switch
							name="send-email"
							checked={sendEmail}
							onCheckedChange={(checked) => setSendEmail(checked)}
							disabled={isAnyLoading || !canUseEmail}
							variant="branded"
							size="small"
						/>
						<span className="text-sm text-secondary">
							Notify{" "}
							<span className="font-medium text-primary">
								{isChangelogUpdateModal
									? changelogEmailRecipientsCount
									: nonChangelogEmailRecipientsCount}
							</span>{" "}
							{changelogEmailRecipientsCount === 1 ||
							nonChangelogEmailRecipientsCount === 1
								? "subscriber"
								: "subscribers"}{" "}
							via email
						</span>
						{canUseEmail && csvData.length > 1 && (
							<Tooltip label="Download emails as CSV">
								<span>
									<CSVLink
										data={csvData}
										filename="recipients-export.csv"
										onClick={() => {
											toast.success({ title: "Recipients CSV exported" })
										}}
									>
										<IconButton
											Icon={Download}
											variant="ghost"
											iconClassName="text-tertiary group-hover:text-primary"
											className="group"
										/>
									</CSVLink>
								</span>
							</Tooltip>
						)}
						{isChangelogUpdateModal && (
							<Tooltip label="View all recipients">
								<IconButton
									Icon={Users}
									variant={"ghost"}
									iconClassName="text-tertiary group-hover:text-primary"
									className="group"
									onClick={() => {
										window.open("/people", "_blank")
									}}
								/>
							</Tooltip>
						)}
					</div>
					<div className="flex items-center gap-2">
						{canUseEmail && (
							<Tooltip label="Send test email to yourself">
								<Button
									variant="outline"
									size="sm"
									onClick={handleSendTestEmail}
									disabled={isAnyLoading}
									className={cn({
										"opacity-60": isAnyLoading,
									})}
								>
									Send test email
								</Button>
							</Tooltip>
						)}
						{!atLeastProPlan && <UpgradeButton required={pro.andUp} />}
						{atLeastProPlan && !isEmailConfigured && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => {
									void navigate({ to: "/settings/emails" })
								}}
							>
								Set up emails
							</Button>
						)}
					</div>
				</div>
				{isChangelogUpdateModal && (
					<div className="flex items-center justify-between gap-2">
						<div className="flex flex-1 items-center gap-2">
							<Switch
								name="send-slack"
								checked={sendSlack}
								onCheckedChange={(checked) => setSendSlack(checked)}
								disabled={isAnyLoading || !canUseSlack}
								variant="branded"
								size="small"
							/>
							<span className="text-sm text-secondary">
								Notify{" "}
								{slackChannelCount > 0 ? (
									<SlackBroadcastChannelsPopover
										channels={slackChannelsQuery.data?.channels ?? []}
									>
										<button
											type="button"
											className="rounded-sm underline decoration-gray-300 decoration-dotted underline-offset-4 hover:text-primary hover:decoration-gray-500 dark:decoration-gray-600"
										>
											<span className="font-medium text-primary">
												{slackChannelCount}
											</span>{" "}
											Slack {slackChannelCount === 1 ? "channel" : "channels"}
										</button>
									</SlackBroadcastChannelsPopover>
								) : (
									<>
										<span className="font-medium text-primary">0</span> Slack
										channels
									</>
								)}
							</span>
						</div>
						<div className="flex items-center gap-2">
							{canUseSlack && (
								<Tooltip label="Send test Slack post to your notification channel">
									<Button
										variant="outline"
										size="sm"
										onClick={handleSendTestSlack}
										disabled={isAnyLoading}
										className={cn({
											"opacity-60": isAnyLoading,
										})}
									>
										Send test Slack
									</Button>
								</Tooltip>
							)}
							{!atLeastProPlan && <UpgradeButton required={pro.andUp} />}
							{atLeastProPlan && !isSlackConfigured && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => {
										void navigate({ to: "/settings/integrations/slack" })
									}}
								>
									Set up Slack
								</Button>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)

	return (
		<Dialog
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			title={title}
			description={descriptionWithCounts}
			size="lg"
			className="!max-w-[min(38rem,94vw)]"
			onOpenAutoFocus={(e) => e.preventDefault()}
			footer={
				<div className="flex w-full items-center justify-end gap-2">
					{isChangelogUpdateModal ? (
						<Button
							variant="default"
							Icon={PaperPlane}
							disabled={isAnyLoading}
							isLoading={isAnyLoading}
							onClick={executeSend}
						>
							{sendEmail || sendSlack ? "Publish and notify" : "Publish"}
						</Button>
					) : (
						// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
						<span className="flex" onClick={explainSendBlocker}>
							<Button
								variant="default"
								Icon={PaperPlane}
								disabled={isAnyLoading || !!sendBlocker}
								isLoading={isAnyLoading}
								onClick={executeSend}
								className={cn({
									"pointer-events-none opacity-60": !!sendBlocker,
								})}
							>
								Notify
							</Button>
						</span>
					)}
				</div>
			}
		>
			{!isChangelogUpdateModal && (
				<div className="mb-4 min-h-[120px] overflow-hidden rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700/80 dark:bg-gray-800 [&_.ProseMirror_p]:!py-0">
					<DefaultEditor
						key={`message-editor-${entity.type}-${entity.id}`}
						autofocus={false}
						initialContent={message || ""}
						placeholder="Write a message..."
						onUpdate={({ editor }) => setMessage(editor.getHTML())}
						editable={true}
						className="min-h-[120px] w-full max-w-none px-3 pb-2 pt-1"
					/>
				</div>
			)}
			{showsEmailDetails && (
				<Form
					id="broadcast-email-details"
					form={form}
					onSubmit={(_values, e) => {
						if (e) e.preventDefault()
					}}
				>
					<Accordion
						type="single"
						collapsible
						value={detailsOpen ? "details-dropdown" : ""}
						onValueChange={(value) =>
							setDetailsOpen(value === "details-dropdown")
						}
					>
						<AccordionItem className="border-none" value="details-dropdown">
							<AccordionPrimitive.Header className="flex">
								<AccordionPrimitive.Trigger asChild>
									<Button variant="outline" size="sm" className="gap-1">
										<span>Edit subject, sender & from</span>
										<motion.span
											animate={{ rotate: detailsOpen ? 90 : 0 }}
											transition={{
												duration: 0.2,
												ease: [0.23, 1, 0.32, 1],
											}}
											className="flex items-center justify-center text-secondary"
										>
											<CollapseArrowIcon className="h-4 w-4" />
										</motion.span>
									</Button>
								</AccordionPrimitive.Trigger>
							</AccordionPrimitive.Header>
							<AccordionContent>
								<div className="mt-2 space-y-4 rounded-lg border border-gray-200 bg-gray-100 p-4 dark:border-gray-700 dark:bg-gray-800">
									<div className="flex flex-row items-center gap-2">
										<Label
											htmlFor="email-subject"
											className="min-w-16 text-secondary"
										>
											Subject
										</Label>
										<FormInput
											id="email-subject"
											name="subject"
											placeholder={
												isChangelogUpdateModal
													? `New: ${entity.name}`
													: entity.name
											}
											disabled={isAnyLoading}
											className="w-full min-w-16"
											variant="borderless"
											maxLength={CHANGELOG_TITLE_MAXIMUM_LENGTH}
										/>
									</div>
									<div className="flex flex-row items-center gap-2">
										<Label
											htmlFor="sender-name"
											className="min-w-16 text-secondary"
										>
											Sender
										</Label>
										<FormInput
											id="sender-name"
											name="senderName"
											placeholder={defaultSenderName || "Workspace name"}
											disabled={isAnyLoading}
											className="w-full"
											variant="borderless"
											maxLength={SENDER_NAME_MAX_LENGTH}
										/>
									</div>
									<div className="flex flex-row items-center gap-2">
										<Label className="min-w-16 text-secondary">From</Label>
										{fromOptions.length > 1 ? (
											<DropdownMenu
												contentProps={{ align: "start" }}
												trigger={
													<button
														type="button"
														disabled={isAnyLoading}
														className="group -ml-1.5 inline-flex h-7 min-w-48 cursor-default items-center gap-1.5 rounded-md px-1.5 text-sm outline-none hover:bg-gray-150 focus-visible:ring-2 focus-visible:ring-gray-500/40 data-[state=open]:bg-gray-150 dark:hover:bg-gray-700 dark:data-[state=open]:bg-gray-700"
													>
														<span className="truncate">{fromEmail}</span>
														<ChevronDownIcon
															strokeWidth={1.75}
															className="ml-auto h-3 w-3 flex-none text-secondary [transform-box:fill-box] group-data-[state=open]:rotate-180"
															aria-hidden
														/>
													</button>
												}
											>
												<DropdownMenuGroup>
													{fromOptions.map((option) => (
														<DropdownMenuItem
															key={option.email}
															IconRight={
																option.email === fromEmail
																	? CheckIcon
																	: undefined
															}
															onSelect={() => setFromOverride(option.email)}
														>
															<span className="flex min-w-0 items-baseline gap-1.5">
																<span className="truncate">{option.email}</span>
																{option.isBroadcastDefault && (
																	<span className="flex-none text-[11px] text-tertiary">
																		Default
																	</span>
																)}
															</span>
														</DropdownMenuItem>
													))}
												</DropdownMenuGroup>
											</DropdownMenu>
										) : (
											<span className="text-sm">{fromEmail}</span>
										)}
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				</Form>
			)}
		</Dialog>
	)
}
